import React from 'react';
import styled from 'styled-components';

// The labelled segmented control, and the status options it is nearly always driving.
//
// Extracted from SpecList when the fetch mode needed the same thing for the same reason: choosing
// which statuses a list shows. Two copies of sixty lines of styled-components would have drifted, and
// the dot colours in particular have to keep agreeing with SpecStatusBadge.

// Shared control height, so a segmented control and a text field on the same row line up exactly.
export const CONTROL_HEIGHT = '40px';

// Exported as well: a labelled group is what any control on this filter row needs, segmented or not
// — SpecList's search field uses the same pair so the two line up.
export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const GroupLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${({ theme }) => theme.themeColors.secondaryText};
`;

// `align-self: flex-start` is load-bearing. FilterGroup is a flex column, so its children stretch to
// its width by default — and in SpecList that width is content-sized because FilterGroup sits in a
// flex row, while a caller that drops this into a plain block gets a border stretched across the page
// with the buttons huddled at the left of it.
const Segmented = styled.div`
  display: inline-flex;
  align-self: flex-start;
  height: ${CONTROL_HEIGHT};
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
`;

// `$width` makes every segment the same size, which reads as a switch rather than as a row of
// differently-sized buttons. Opt-in: SpecList's Status control has five segments and sits beside a
// search field, so pinning them all to one width there would push that row into wrapping.
const SegButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: ${({ $width }) => ($width ? 'center' : 'flex-start')};
  gap: 7px;
  height: 100%;
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  padding: 0 14px;
  border: 0;
  border-right: 1px solid ${({ theme }) => theme.themeColors.border};
  background: ${({ $active, theme }) => ($active ? theme.themeColors.primaryCtaButton : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.themeColors.buttonText : theme.themeColors.text)};
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;

  &:last-child {
    border-right: 0;
  }

  &:hover {
    background: ${({ $active, theme }) =>
      $active
        ? theme.themeColors.primaryCtaButton
        : theme.themeColors.hoverComponentBackground || theme.themeColors.componentNoOpacity};
  }

  /* The ring follows the TEXT, not the accent — same rule the dot above follows, and for the same
     reason. The offset is negative so the ring sits inside the group's rounded border instead of
     straddling it, which means on the active segment it is painted over that segment's own accent
     fill: one colour for both left a 1:1 ring, i.e. no visible focus at all on whichever segment is
     currently selected. That is the default state of every one of these controls, so it was the
     first tab stop that showed nothing, and activating a segment made the ring vanish under the
     finger. */
  &:focus-visible {
    outline: 2px solid
      ${({ $active, theme }) => ($active ? theme.themeColors.buttonText : theme.themeColors.primaryCtaButton)};
    outline-offset: -2px;
  }
`;

// On the active (accent-filled) segment the dot switches to the button-text colour so it stays
// visible against the accent; inactive segments keep the semantic status colour.
const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $active, $token, theme }) =>
    $active ? theme.themeColors.buttonText : theme.themeColors[$token] || theme.themeColors.secondaryText};
`;

// The lifecycle statuses, with the dot colour each one carries (mirrors SpecStatusBadge). A caller
// that wants a subset picks from this rather than restating the tokens.
//
// `all` takes the `text` token rather than a literal black: it is the strongest foreground colour in
// whichever theme is active, so the dot reads as black on the light themes and stays visible on the
// dark one. A hardcoded #000 would disappear into a dark background. (The ACTIVE state is unaffected
// either way — every dot switches to buttonText to stay visible against the accent fill.)
export const STATUS_SEGMENTS = [
  { key: 'all', label: 'All', token: 'text' },
  { key: 'draft', label: 'Draft', token: 'secondaryText' },
  { key: 'locked', label: 'Locked', token: 'primaryCtaButton' },
  { key: 'approved', label: 'Approved', token: 'positive' },
  { key: 'retired', label: 'Retired', token: 'negative' },
];

export const statusSegments = (keys) => keys.map((key) => STATUS_SEGMENTS.find((s) => s.key === key)).filter(Boolean);

// `hideLabel` drops the uppercase GroupLabel and nothing else: the group keeps `label` as its
// accessible name, and the caller draws its own label — a form that sets this control beside a
// TextField wants the two labelled the same way, not one in 11px caps and one in sentence case.
export const SegmentedControl = ({ label, options, active, onSelect, withDots, segmentWidth, hideLabel = false }) => (
  <FilterGroup>
    {!hideLabel && <GroupLabel>{label}</GroupLabel>}
    <Segmented role="group" aria-label={label}>
      {options.map((opt) => {
        const isActive = active === opt.key;
        return (
          <SegButton
            key={opt.key}
            type="button"
            aria-pressed={isActive}
            $active={isActive}
            $width={segmentWidth}
            onClick={() => onSelect(opt.key)}
          >
            {withDots && <Dot $token={opt.token} $active={isActive} />}
            {opt.label}
          </SegButton>
        );
      })}
    </Segmented>
  </FilterGroup>
);
